/**
 * Client portal grant resolution — merges contact_user_access,
 * company_user_access, and RLS-visible contacts into one client-facing model.
 *
 * Mirrors Postgres `has_contact_access()` + `client_can_create_ticket_for_contact()`.
 * See migrations 20260618130000_company_user_access.sql and
 * 20260619130000_client_access_levels_ticket_create_gate.sql.
 */

import type { AccessLevel } from "@/lib/types/database";
import type {
  AccessibleContact,
  ClientPortalGrantSummary,
} from "@/lib/hooks/api/client-portal/types";

export interface ContactGrantRow {
  contact_id: string;
  access_level: string | null;
  is_active?: boolean | null;
  expires_at?: string | null;
  contacts:
    | {
        id: string;
        name: string | null;
        company_name: string | null;
        company_id: string | null;
      }
    | {
        id: string;
        name: string | null;
        company_name: string | null;
        company_id: string | null;
      }[]
    | null;
}

export interface CompanyGrantRow {
  company_id: string;
  access_level: string | null;
  is_active?: boolean | null;
  expires_at?: string | null;
  companies:
    | { id: string; name: string | null }
    | { id: string; name: string | null }[]
    | null;
}

export interface RlsContactRow {
  id: string;
  name: string | null;
  company_name: string | null;
  company_id: string | null;
}

function pickOne<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function isGrantActive(
  row: { is_active?: boolean | null; expires_at?: string | null },
  nowIso: string
): boolean {
  if (row.is_active === false) return false;
  if (row.expires_at && row.expires_at <= nowIso) return false;
  return true;
}

/**
 * Build the contact list a client user may view on the dashboard.
 * `rlsContacts` must come from a user-authenticated Supabase client so RLS
 * applies (contacts the user cannot see are already excluded).
 */
export function buildAccessibleContactsFromGrants(
  contactGrants: ContactGrantRow[],
  companyGrants: CompanyGrantRow[],
  rlsContacts: RlsContactRow[],
  nowIso: string = new Date().toISOString()
): AccessibleContact[] {
  const contactLevelById = new Map<string, string>();
  for (const row of contactGrants) {
    if (!isGrantActive(row, nowIso)) continue;
    if (row.access_level) {
      contactLevelById.set(row.contact_id, row.access_level);
    }
  }

  const companyLevelById = new Map<string, string>();
  for (const row of companyGrants) {
    if (!isGrantActive(row, nowIso)) continue;
    if (row.access_level) {
      companyLevelById.set(row.company_id, row.access_level);
    }
  }

  return rlsContacts.map((contact) => {
    const directLevel = contactLevelById.get(contact.id);
    const companyLevel = contact.company_id
      ? companyLevelById.get(contact.company_id)
      : undefined;
    const access_level = (directLevel ?? companyLevel ?? "limited") as AccessLevel;
    const grant_scope = directLevel ? ("contact" as const) : ("company" as const);

    return {
      contact_id: contact.id,
      contact_name: contact.name || "Unknown Contact",
      company_name: contact.company_name || "",
      access_level,
      grant_scope,
      total_tickets: 0,
      open_tickets: 0,
      total_hours: 0,
      total_billed: 0,
    };
  });
}

/** Summary of explicit grant rows for the "your access" panel (not per-contact expansion). */
export function buildClientPortalGrantSummary(
  contactGrants: ContactGrantRow[],
  companyGrants: CompanyGrantRow[],
  nowIso: string = new Date().toISOString()
): ClientPortalGrantSummary {
  const companyGrantsSummary = companyGrants
    .filter((row) => isGrantActive(row, nowIso))
    .map((row) => {
      const company = pickOne(row.companies);
      return {
        company_id: row.company_id,
        company_name: company?.name ?? null,
        access_level: (row.access_level ?? "read_only") as AccessLevel,
      };
    });

  const contactGrantsSummary = contactGrants
    .filter((row) => isGrantActive(row, nowIso))
    .map((row) => {
      const contact = pickOne(row.contacts);
      return {
        contact_id: row.contact_id,
        contact_name: contact?.name ?? null,
        company_name: contact?.company_name ?? null,
        access_level: (row.access_level ?? "limited") as AccessLevel,
      };
    });

  const hasAccess =
    companyGrantsSummary.length > 0 || contactGrantsSummary.length > 0;

  return {
    companyGrants: companyGrantsSummary,
    contactGrants: contactGrantsSummary,
    hasAccess,
  };
}
